import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	useBulkRetriggerLeadScoringMutation,
	useSearchForCompanyEmailAndPhoneMutation,
	useSearchForEmailsMutation,
	useSearchForPhonesMutation,
} from "@/pages/Private/redux/list/list.api";

import { ListPerson } from "../../../schema/list";
import {
	BulkContactInfoScope,
	buildBulkContactInfoRequest,
	resolveBulkContactInfoIds,
	runBulkContactInfoLookup,
} from "../../../utils/bulkContactInfoRequest";
import { ListLookupAction, LookupCreditConfirm } from "../../CreditConfirm/LookupCreditConfirm";
import { ModalBulkLeadScoring } from "../../Scoring/ModalBulkLeadScoring";

export interface UseBulkCreditActionsParams {
	data: ListPerson[] | undefined;
	selectedItems: number[];
	/** The row a row action picked; the lookups run on it alone. */
	selectedItemForConfirmation: ListPerson | null;
	clearItemForConfirmation: () => void;
	/** The table's scope, as every bulk lookup sends it. */
	scope: BulkContactInfoScope;
	/** Leads selected, as the table counts them (the list's total for a select-all). */
	selectedCount: number;
	notify: (message: string) => void;
	/** Ends a select-all and goes back to page selection, after an action ran. */
	resetSelection: () => void;
}

/** What the table shows and does after each lookup ran. */
const LOOKUP_MESSAGES: Record<ListLookupAction, string> = {
	EMAIL: "Searching for email addresses...",
	PHONE: "Searching for phone numbers...",
	COMPANY_CONTACT: "Searching for company email & phone...",
};

/**
 * The list table's credit-spending bulk actions behind the credit-confirm modal (PREC-4263): the
 * email, phone and company email & phone lookups and the lead scoring retrigger. Each opens its
 * dialog, which quotes the exact request the action then sends, and runs the action from it; a
 * refusal stays in the dialog. Extracted from UnassignedTable, which wires the footer to `open*`
 * and renders `modals`.
 */
export function useBulkCreditActions({
	data,
	selectedItems,
	selectedItemForConfirmation,
	clearItemForConfirmation,
	scope,
	selectedCount,
	notify,
	resetSelection,
}: UseBulkCreditActionsParams) {
	const { t } = useTranslation();
	const [lookup, setLookup] = useState<ListLookupAction | null>(null);
	const [showLeadScoring, setShowLeadScoring] = useState(false);

	const [searchForEmails, { isLoading: emailIsLoading }] = useSearchForEmailsMutation();
	const [searchForPhones, { isLoading: phoneIsLoading }] = useSearchForPhonesMutation();
	const [searchForCompanyEmailAndPhone, { isLoading: companyIsLoading }] =
		useSearchForCompanyEmailAndPhoneMutation();
	const [bulkRetriggerLeadScoring, { isLoading: leadScoringIsLoading }] =
		useBulkRetriggerLeadScoringMutation();

	const mutations = {
		EMAIL: searchForEmails,
		PHONE: searchForPhones,
		COMPANY_CONTACT: searchForCompanyEmailAndPhone,
	};

	const runLookup = async (action: ListLookupAction) => {
		await runBulkContactInfoLookup(mutations[action], {
			data,
			selectedItems,
			selectedItemForConfirmation,
			scope,
			message: LOOKUP_MESSAGES[action],
			notify,
			onSettled: clearItemForConfirmation,
		});
		resetSelection();
		setLookup(null);
	};

	const runLeadScoring = async (rescore: boolean) => {
		const result = await bulkRetriggerLeadScoring({
			...scope,
			listPersonIds: selectedItems,
			rescore,
			assignStatus: scope.assignStatus ? [scope.assignStatus] : undefined,
		}).unwrap();

		notify(
			t("inbox.bulkLeadScoringSuccess", { scheduled: result.scheduled, skipped: result.skipped })
		);
		resetSelection();
		setShowLeadScoring(false);
	};

	const lookupRequest = buildBulkContactInfoRequest(
		resolveBulkContactInfoIds(data, selectedItems, selectedItemForConfirmation),
		scope
	);

	const modals: ReactNode = (
		<>
			{lookup && (
				<LookupCreditConfirm
					action={lookup}
					request={lookupRequest}
					selectedCount={selectedCount}
					targetName={selectedItemForConfirmation?.prospect?.name}
					onClose={() => setLookup(null)}
					onConfirm={() => runLookup(lookup)}
				/>
			)}
			{showLeadScoring && (
				<ModalBulkLeadScoring
					request={buildBulkContactInfoRequest(selectedItems, scope)}
					selectedCount={selectedCount}
					onClose={() => setShowLeadScoring(false)}
					onConfirm={runLeadScoring}
				/>
			)}
		</>
	);

	return {
		openLookup: setLookup,
		openLeadScoring: () => setShowLeadScoring(true),
		isLoading: {
			EMAIL: emailIsLoading,
			PHONE: phoneIsLoading,
			COMPANY_CONTACT: companyIsLoading,
			leadScoring: leadScoringIsLoading,
		},
		modals,
	};
}
